import {AfterViewInit, Component, inject, isDevMode, OnDestroy, OnInit, signal, WritableSignal} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import { PrimusBackendInstanceService, PrimusInstanceDetails } from '../../core/primus-backend-instance.service';
import {TranslateService} from '@ngx-translate/core';
import {TranslationSetterService} from '../../core/translation-setter.service';
import {logout, login} from '@ekultur/authentication';
import {environment} from "../../../environments/environment";
import {HibernationService} from '../hibernation.service';
import {HibernationFlowService} from '../hibernation-flow.service';
import {Subscription} from 'rxjs';

@Component({
    selector: 'app-choose-backend-api',
    templateUrl: './choose-backend-api.component.html',
    styleUrls: ['./choose-backend-api.component.scss'],
    standalone: false
})
export class ChooseBackendApiComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly MUSEUM_SELECTOR_PARAM = 'selectMuseum';

  // Injections
  private readonly primusBackendInstanceService: PrimusBackendInstanceService = inject(PrimusBackendInstanceService);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly translationSetter: TranslationSetterService = inject(TranslationSetterService);
  private readonly hibernationService: HibernationService = inject(HibernationService);
  private readonly hibernationFlow: HibernationFlowService = inject(HibernationFlowService);

  public showUrl: boolean = isDevMode();
  public servers: Array<PrimusInstanceDetails>;
  public hidden: boolean;
  public txtHeader: string;
  public serversLoaded: boolean;
  private authStateSubscription: Subscription | null = null;

  selectedInstanceLoading: WritableSignal<string> = signal('none');


  constructor() {
    this.translationSetter.setTranslations();
  }

  ngOnInit(): void {
    this.translate.get('TRANS__SHARED_CHOOSE_BACKEND__PRIMUS_INSTANCES').subscribe(s => {
      this.txtHeader = s;
    });
    this.ensureBackendSelectorParam();
  }

  async ngAfterViewInit(): Promise<void> {
    this.authStateSubscription = this.primusBackendInstanceService.authenticated.subscribe(async (state) => {
      if (state.user && !this.hidden) {
        this.servers = await this.primusBackendInstanceService.getUserPrimusServers();

        // If only granted access to a single server, redirect to that server automatically.
        if (this.servers?.length === 1) {
          const server = this.servers[0] as any as PrimusInstanceDetails;
          this.authApi(server).then();
        }

        this.serversLoaded = true;
      }
    });
  }

  ngOnDestroy(): void {
    // Unsubscribe only this component's subscription.
    // Do not unsubscribe the shared BehaviorSubject in the service.
    this.authStateSubscription?.unsubscribe();
    this.serversLoaded = false;
    this.hidden = false;
  }

  async pingServers() {
    for (let i = 0, max = this.servers.length; i < max; i++) {
      const server = this.servers[i];
      server['online'] = true;
      server['checked'] = true;

      if (server.name.toLowerCase() === 'localhost') {
        continue;
      }

      let dbOnline = false;
      let solrOnline = false;

      try {
        const ping = await fetch(server.api + '/healthcheck/', { method: 'GET' });
        const data = await ping.json();

        if (data['db'].conn === 'ok') {
          dbOnline = true;
        }

        const solr = data['solr'];
        if (solr.add === 'ok' &&
          solr.conn === 'ok' &&
          solr.delete === 'ok' &&
          solr.query === 'ok') {
          solrOnline = true;
        }
      } catch (e) {
        server['checked'] = true;
      } finally {
        server['online'] = dbOnline && solrOnline;
        server['checked'] = true;
      }
    }
  }

  logoutUser() {
    const eCultureLogoutCallback = () => {
      PrimusBackendInstanceService.cleanInstanceDetails();

      setTimeout(() => {
        login({
          locationPostLogin: null,
          redirectUri: null,
          loginHost: environment.ekulturLoginGatewayURL
        })
      }, 500);
    }

    logout({
      signal: null,
      apiGateway: environment.ekulturAPIGatewayURL,
      onLogout: eCultureLogoutCallback
    });
  }

  onServerSelect(change: string) {
    this.selectedInstanceLoading.set(change);
    const apiUrl = change;
    const server = this.servers.find(s => s.api === apiUrl) as any as PrimusInstanceDetails;
    this.authApi(server).then();
  }

  private async authApi(server: PrimusInstanceDetails) {
    try {
      await this.clearBackendSelectorParam();
      // Exit selector mode immediately after explicit museum choice so wake/splash UI
      // can render without waiting for downstream auth-state updates.
      this.hibernationFlow.setBackendSelectorVisible(false);
      // User explicitly selected a backend from selector.
      // Clear previous wake timeout/error so a stale message from an old backend
      // does not block or leak into the new wake/auth attempt.
      this.hibernationService.clearWakeupError();
      this.primusBackendInstanceService.apiAuthenticate(server, () => {
        this.selectedInstanceLoading.set('none');
        // Use assign so the selector stays in history and browser Back returns to it.
        window.location.assign('/home');
      });
    } catch (e) {
      console.error(e);
      this.selectedInstanceLoading.set('none');
      this.serversLoaded = false;
     }
  }

  private async clearBackendSelectorParam() {
    if (!this.route.snapshot.queryParamMap.has(ChooseBackendApiComponent.MUSEUM_SELECTOR_PARAM)) {
      return;
    }
    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        [ChooseBackendApiComponent.MUSEUM_SELECTOR_PARAM]: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private ensureBackendSelectorParam() {
    if (this.route.snapshot.queryParamMap.has(ChooseBackendApiComponent.MUSEUM_SELECTOR_PARAM)) {
      return;
    }

    // Add a marker so Back can return to the selector without adding a new history entry.
    // Merge any existing params and replace the URL so back-nav includes selectMuseum=1
    // and forces the selector (but doesn't add another history entry).
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        [ChooseBackendApiComponent.MUSEUM_SELECTOR_PARAM]: '1',
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
